import { useCallback, useEffect, useMemo, useState } from "react";

import type { EnvironmentId } from "@cmmd-forge/contracts";
import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime";
import { useGitStatus } from "../../lib/gitStatusState";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import type { Project } from "../../types";
import type { ExecutionOption } from "./composerChips";
import { useUiStateStore } from "../../uiStateStore";
import { useDesktopRuntimeCatalog } from "./useDesktopRuntimeCatalog";
import { useDraftPickerOptions } from "./useDraftPickerOptions";
import { useDraftBranchSelection } from "./useDraftBranchSelection";
import { useDraftProjectResolution } from "./useDraftProjectResolution";
import {
  NO_PROJECT_SELECTION_KEY,
  type ProjectSummary,
  filterProjectsForExecution,
  projectSelectionKey,
  resolveInitialExecutionId,
} from "./NewThreadLanding.logic";

interface InitialProjectPreselectParams {
  readonly projectKey: string | null;
  readonly executionId: string | null;
}

/**
 * `?project=<key>&execution=<id>` preselect a project/execution target on
 * landing (e.g. the sidebar's "+" on a not-yet-connected cloud project routes
 * here instead of provisioning it — see preConnectCloudNewThreadRoute.ts).
 * Read once on mount, same pattern as `qaPrompt`/`firstAccountSetupPrompt`
 * below; applying it is resilient by construction — see the effect that
 * consumes it, which only overrides the default selection once the named
 * project is actually found among the current picker summaries.
 */
function readInitialProjectPreselectParams(): InitialProjectPreselectParams {
  if (typeof window === "undefined") {
    return { projectKey: null, executionId: null };
  }
  const searchParams = new URLSearchParams(window.location.search);
  return {
    projectKey: searchParams.get("project"),
    executionId: searchParams.get("execution"),
  };
}

/**
 * Every project a new thread could run in, from all three catalogues.
 *
 * Local projects come from the store, cloud rows from CMMD's runtime list, and
 * desktop rows from the desktop inventory — three sources that a user thinks of
 * as one list. Gathered together because the picker merges them and every
 * downstream decision works on the merged view.
 */
function useDraftProjectCatalogs({
  activeEnvironmentId,
  environmentStateById,
}: {
  readonly activeEnvironmentId: ReturnType<typeof useStore.getState>["activeEnvironmentId"];
  readonly environmentStateById: ReturnType<typeof useStore.getState>["environmentStateById"];
}) {
  const projects = useMemo<readonly Project[]>(
    () => selectProjectsAcrossEnvironments({ activeEnvironmentId, environmentStateById }),
    [activeEnvironmentId, environmentStateById],
  );

  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();
  const desktopRuntimeCatalog = useDesktopRuntimeCatalog();

  return { projects, cloudCatalog, desktopRuntimeCatalog };
}

/**
 * Keeps the picker's selection honest as the catalogues load.
 *
 * Two corrections, and the second one is the subtle one. If the chosen
 * execution target disappears the selection has to fall back, and the project
 * goes with it — a project belongs to a target, so keeping it would point at a
 * project the new target cannot run.
 *
 * The `?project=` preselect then applies once, and only if the user has not
 * already chosen. Cloud rows arrive over SSE, so without that guard a
 * late-arriving match overwrites a choice made while the list was still
 * loading — reachable from every sidebar "New thread" click once those links
 * started carrying `?project=` (CMMD-2266). A key that never matches simply
 * never applies, which leaves the default resolution untouched rather than
 * rendering a broken state.
 */
function useDraftSelectionSync({
  executionOptions,
  selectedExecutionId,
  setSelectedExecutionId,
  selectedProjectKey,
  setSelectedProjectKey,
  projectSummaries,
  initialProjectPreselect,
  projectPreselectApplied,
  setProjectPreselectApplied,
}: {
  readonly executionOptions: readonly ExecutionOption[];
  readonly selectedExecutionId: string;
  readonly setSelectedExecutionId: (next: string) => void;
  readonly selectedProjectKey: string | null;
  readonly setSelectedProjectKey: (next: string | null) => void;
  readonly projectSummaries: readonly ProjectSummary[];
  readonly initialProjectPreselect: ReturnType<typeof readInitialProjectPreselectParams>;
  readonly projectPreselectApplied: boolean;
  readonly setProjectPreselectApplied: (next: boolean) => void;
}): void {
  useEffect(() => {
    if (executionOptions.some((option) => option.id === selectedExecutionId)) {
      return;
    }
    setSelectedExecutionId(executionOptions[0]?.id ?? "device");
    setSelectedProjectKey(null);
  }, [executionOptions, selectedExecutionId, setSelectedExecutionId, setSelectedProjectKey]);

  // Apply the `?project=` preselect once its target appears among the picker
  // summaries. Resilient by construction: a catalog row that never loads (an
  // unknown/stale project key) simply never matches, so the default selection
  // resolution above is left untouched instead of rendering a broken state.
  useEffect(() => {
    if (projectPreselectApplied) return;
    // A manual pick outranks the URL. `selectedProjectKey` only becomes non-null
    // from `handleSelectProject`/`handleSelectNoProject` (every other writer
    // clears it), so this is specifically "the user already chose". Cloud catalog
    // rows arrive over SSE, so without this a late-arriving match would overwrite
    // a choice the user made while the list was still loading — a race that
    // became reachable from every sidebar "New thread" click once those links
    // started carrying `?project=` (CMMD-2266).
    if (selectedProjectKey !== null) {
      setProjectPreselectApplied(true);
      return;
    }
    const match = projectSummaries.find(
      (summary) => summary.id === initialProjectPreselect.projectKey,
    );
    if (!match) return;
    setSelectedExecutionId(initialProjectPreselect.executionId ?? "cloud");
    setSelectedProjectKey(projectSelectionKey(match));
    setProjectPreselectApplied(true);
  }, [
    initialProjectPreselect,
    projectPreselectApplied,
    projectSummaries,
    selectedProjectKey,
    setProjectPreselectApplied,
    setSelectedExecutionId,
    setSelectedProjectKey,
  ]);
}

/**
 * What the picker has chosen, and the ways it can change.
 *
 * Choosing an execution target clears the project, because a project belongs to
 * a target — keeping it would point at a project the new target cannot run.
 * "No project" is a real selection rather than the absence of one, which is why
 * it has its own key: null means "nothing chosen yet, use the default", and the
 * two must not collapse.
 */
function useDraftPickerSelection({
  initialProjectPreselect,
  defaultExecutionId,
  openAddProjectSources,
  openAddCloudProjectSources,
}: {
  readonly initialProjectPreselect: ReturnType<typeof readInitialProjectPreselectParams>;
  readonly defaultExecutionId: string;
  readonly openAddProjectSources: () => void;
  readonly openAddCloudProjectSources: () => void;
}) {
  const [selectedProjectKey, setSelectedProjectKey] = useState<string | null>(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string>(defaultExecutionId);
  const [projectPreselectApplied, setProjectPreselectApplied] = useState(
    initialProjectPreselect.projectKey === null,
  );

  const handleSelectProject = useCallback((project: ProjectSummary) => {
    setSelectedProjectKey(projectSelectionKey(project));
  }, []);

  const handleSelectExecution = useCallback((executionId: string) => {
    setSelectedExecutionId(executionId);
    setSelectedProjectKey(null);
  }, []);

  const handleAddProject = useCallback(() => {
    if (selectedExecutionId === "cloud") {
      openAddCloudProjectSources();
      return;
    }
    openAddProjectSources();
  }, [openAddCloudProjectSources, openAddProjectSources, selectedExecutionId]);

  const handleSelectNoProject = useCallback(() => {
    setSelectedProjectKey(NO_PROJECT_SELECTION_KEY);
  }, []);

  return {
    selectedProjectKey,
    setSelectedProjectKey,
    selectedExecutionId,
    setSelectedExecutionId,
    projectPreselectApplied,
    setProjectPreselectApplied,
    handleSelectProject,
    handleSelectExecution,
    handleAddProject,
    handleSelectNoProject,
  };
}

export interface DraftProjectChoiceInput {
  readonly activeEnvironmentId: EnvironmentId | null;
  readonly environmentStateById: Parameters<
    typeof useDraftProjectCatalogs
  >[0]["environmentStateById"];
  readonly isCloudControlSurface: boolean;
  readonly projectOrder: ReturnType<typeof useUiStateStore.getState>["projectOrder"];
  readonly lastActiveProject: ReturnType<typeof useUiStateStore.getState>["lastActiveProject"];
  readonly openAddProjectSources: () => void;
  readonly openAddCloudProjectSources: () => void;
}

/**
 * Which project this draft will run in, and on what branch.
 *
 * The whole selection story, from the three catalogues through the picker's
 * options, the user's choice, and the URL preselect, down to the one resolved
 * project and its branch. They were separate hooks in the landing component,
 * but none of them is separately answerable: the options depend on the
 * selection (choosing "no project" is what expands the aggregate Cloud entry),
 * the selection is repaired against the options, and everything downstream of
 * "which project" — git status, default branch, branch choice — only has an
 * answer once that one is settled.
 */
export function useDraftProjectChoice(input: DraftProjectChoiceInput) {
  const savedEnvironmentsById = useSavedEnvironmentRegistryStore((state) => state.byId);
  const initialProjectPreselect = useMemo(readInitialProjectPreselectParams, []);

  const { projects, cloudCatalog, desktopRuntimeCatalog } = useDraftProjectCatalogs({
    activeEnvironmentId: input.activeEnvironmentId,
    environmentStateById: input.environmentStateById,
  });

  const selection = useDraftPickerSelection({
    initialProjectPreselect,
    defaultExecutionId: resolveInitialExecutionId({
      activeEnvironmentId: input.activeEnvironmentId,
      cloudCatalogRowCount: cloudCatalog.rows.length,
      isCloudControlSurface: input.isCloudControlSurface,
    }),
    openAddProjectSources: input.openAddProjectSources,
    openAddCloudProjectSources: input.openAddCloudProjectSources,
  });
  const noWorkspaceSelected = selection.selectedProjectKey === NO_PROJECT_SELECTION_KEY;

  const { executionOptions, projectSummaries } = useDraftPickerOptions({
    activeEnvironmentId: input.activeEnvironmentId,
    cloudCatalog,
    desktopRuntimeCatalog,
    isCloudControlSurface: input.isCloudControlSurface,
    noWorkspaceSelected,
    projects,
    savedEnvironmentsById,
  });

  useDraftSelectionSync({
    executionOptions,
    selectedExecutionId: selection.selectedExecutionId,
    setSelectedExecutionId: selection.setSelectedExecutionId,
    selectedProjectKey: selection.selectedProjectKey,
    setSelectedProjectKey: selection.setSelectedProjectKey,
    projectSummaries,
    initialProjectPreselect,
    projectPreselectApplied: selection.projectPreselectApplied,
    setProjectPreselectApplied: selection.setProjectPreselectApplied,
  });

  const filteredProjectSummaries = useMemo<ProjectSummary[]>(
    () => filterProjectsForExecution(projectSummaries, selection.selectedExecutionId),
    [projectSummaries, selection.selectedExecutionId],
  );

  // The project the user was last actually in. `projectOrder` is only the
  // persisted sidebar drag arrangement, so without this a bare `/new` always
  // opened on whichever project sits first in the sidebar — which then
  // repointed provider access at that project's runtime (CMMD-2266).
  const { activeProject, activeProjectPresentation, activeCloudCatalogRow } =
    useDraftProjectResolution({
      cloudCatalog,
      filteredProjectSummaries,
      lastActiveProject: input.lastActiveProject,
      projectOrder: input.projectOrder,
      projectSummaries,
      projects,
      selectedProjectKey: selection.selectedProjectKey,
    });

  const activeProjectGitStatus = useGitStatus({
    environmentId: activeProject?.environmentId ?? null,
    cwd: activeProjectPresentation.gitCwd,
  });
  const activeProjectDefaultBranch = activeProjectGitStatus.data?.defaultBranch ?? null;
  const branchSelection = useDraftBranchSelection({
    projectKey: activeProjectPresentation.key,
    projectDefaultBranch: activeProjectDefaultBranch,
  });

  // The picker's raw setters and preselect bookkeeping stay inside: they exist
  // so this hook can repair its own selection against the options, and a caller
  // that set them directly would be writing state this hook immediately
  // recomputes.
  return {
    selectedExecutionId: selection.selectedExecutionId,
    handleSelectProject: selection.handleSelectProject,
    handleSelectExecution: selection.handleSelectExecution,
    handleAddProject: selection.handleAddProject,
    handleSelectNoProject: selection.handleSelectNoProject,
    ...branchSelection,
    noWorkspaceSelected,
    projects,
    desktopRuntimeCatalog,
    executionOptions,
    filteredProjectSummaries,
    activeProject,
    activeProjectPresentation,
    activeCloudCatalogRow,
    activeProjectDefaultBranch,
  };
}
